import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  FaFilter,
  FaArrowRight,
  FaBullhorn,
  FaTimes,
  FaCalendarDay,
  FaFolderOpen,
} from "react-icons/fa";
import "./News.css";
import img from "../../assets/breadcrumb/News.jpg"; // Your breadcrumb image

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const formatFullDate = (dateString) => {
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(dateString).toLocaleDateString("en-US", options);
};

// Formatter for card date (e.g., "MAR 15")
const formatCardDate = (dateString) => {
  const d = new Date(dateString);
  const month = d.toLocaleString("default", { month: "short" }).toUpperCase();
  const day = d.getDate();
  return `${month} ${day}`;
};

function News() {
  const [newsData, setNewsData] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/news/public`);
        const data = await response.json();
        setNewsData(data);
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const categories = ["All", ...new Set(newsData.map((item) => item.category))];
  const filteredNews =
    activeFilter === "All"
      ? newsData
      : newsData.filter((item) => item.category === activeFilter);

  const handleReadMore = (e, newsItem) => {
    e.stopPropagation();
    setSelectedNews(newsItem);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") handleCloseModal();
    };
    window.addEventListener("keydown", handleEsc);
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  if (loading)
    return <div className="news-and-events-loading">Loading News...</div>;

  return (
    <>
      <Helmet>
        <title>
          News & Announcements | Sucheta Kriplani Teacher Training College
          (SKTTC), Jodhpur
        </title>

        <meta
          name="description"
          content="Stay updated with the latest news, admission notices, PTET updates, academic announcements, campus events, and activities at Sucheta Kriplani Teacher Training College (SKTTC), Manaklao, Jodhpur."
        />

        <meta
          name="keywords"
          content="SKTTC News, Sucheta Kriplani College Announcements, B.Ed College Jodhpur News, PTET Updates, JNVU Affiliated College News, Manaklao College Events"
        />

        <link rel="canonical" href="https://suchetabedcollege.org/news" />
      </Helmet>

      <div className="news-and-events-wrapper">
        {/* 
         UPDATED HERO SECTION:
         Using <img src={img} /> instead of background-image 
      */}
        <div className="news-and-events-hero">
          <img
            src={img}
            alt="College News"
            className="news-and-events-hero-img"
          />

          <div className="news-and-events-hero-overlay">
            <h1 className="news-and-events-main-title">
              College News & Announcements
            </h1>
            <p className="news-and-events-subtitle">
              Stay informed with official college updates and campus highlights
            </p>
            <div className="news-and-events-breadcrumb">
              <span className="news-and-events-bread-item">Home</span>
              <span className="news-and-events-sep">/</span>
              <span className="news-and-events-bread-active">College News</span>
            </div>
          </div>
        </div>

        <div className="news-and-events-container">
          <div className="news-and-events-filter-bar">
            <div className="news-and-events-filter-label">
              <FaFilter /> <span>Filter News:</span>
            </div>
            <div className="news-and-events-filter-buttons">
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`news-and-events-filter-btn ${activeFilter === cat ? "active" : ""}`}
                  onClick={() => setActiveFilter(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="news-and-events-grid">
            {filteredNews.length > 0 ? (
              filteredNews.map((news) => (
                <div key={news._id} className="news-and-events-card">
                  <img
                    src={`${API_BASE_URL}${news.image}`}
                    alt={news.title}
                    className="news-and-events-card-bg"
                    onError={(e) =>
                      (e.target.src =
                        "https://via.placeholder.com/400x300?text=No+Image")
                    }
                  />

                  <div className="news-and-events-initial-content">
                    <h3 className="news-and-events-initial-title">
                      {news.title}
                    </h3>
                    <span className="news-and-events-initial-date">
                      {formatCardDate(news.publishDate)}
                    </span>
                  </div>

                  <div className="news-and-events-hover-overlay">
                    <div className="news-and-events-overlay-box">
                      <h3 className="news-and-events-overlay-title">
                        {news.title}
                      </h3>
                      <button
                        className="news-and-events-overlay-btn"
                        onClick={(e) => handleReadMore(e, news)}
                      >
                        Read More{" "}
                        <FaArrowRight className="news-and-events-arrow-icon" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="news-and-events-no-data">
                <FaBullhorn />
                <p>No news items found.</p>
              </div>
            )}
          </div>
        </div>

        {isModalOpen && selectedNews && (
          <div
            className="news-and-events-modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) handleCloseModal();
            }}
          >
            <div className="news-and-events-modal-article">
              <div className="news-and-events-modal-header">
                <button
                  className="news-and-events-modal-close-btn"
                  onClick={handleCloseModal}
                >
                  <FaTimes />
                </button>
                <h2 className="news-and-events-modal-title">
                  {selectedNews.title}
                </h2>
                <div className="news-and-events-modal-meta-row">
                  <span className="news-and-events-modal-meta-text">
                    <FaCalendarDay /> {formatFullDate(selectedNews.publishDate)}
                  </span>
                  <span className="news-and-events-modal-sep">|</span>
                  <span className="news-and-events-modal-meta-text">
                    <FaFolderOpen /> {selectedNews.category}
                  </span>
                </div>
              </div>
              <div className="news-and-events-article-body">
                <img
                  src={`${API_BASE_URL}${selectedNews.image}`}
                  alt={selectedNews.title}
                  className="news-and-events-article-float-img"
                  onError={(e) =>
                    (e.target.src =
                      "https://via.placeholder.com/400x300?text=No+Image")
                  }
                />
                <div className="news-and-events-article-text-content">
                  {selectedNews.content}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default News;
